import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { SiBmw, SiAudi, SiVolkswagen, SiVolvo, SiFord, SiRenault, SiPeugeot, SiPorsche, SiToyota } from "react-icons/si";
import { Cpu, Gauge, Coins } from "lucide-react";

const ICONS = {
  bmw: SiBmw, audi: SiAudi, volkswagen: SiVolkswagen, vw: SiVolkswagen,
  volvo: SiVolvo, ford: SiFord, renault: SiRenault, peugeot: SiPeugeot,
  porsche: SiPorsche, toyota: SiToyota,
};

const brandIcon = (brand = "") => {
  const key = brand.toLowerCase().split(/[\s-]/)[0];
  return ICONS[key] || null;
};

export const ProcessedFilesFeed = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/public/recent").then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section className="px-6 md:px-12 py-20 border-t border-white/5" data-testid="processed-feed">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-3 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-signal-success animate-pulse-glow" /> // Activité atelier
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">Fichiers traités récemment</h2>
        </div>
        <p className="text-zinc-400 max-w-sm text-sm">
          Un aperçu en direct des dernières reprogrammations livrées par notre atelier. Calibrations vérifiées, prêtes à flasher.
        </p>
      </div>

      <div className="glass rounded-md overflow-hidden">
        {items.map((it, i) => {
          const Icon = brandIcon(it.brand);
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.35, delay: i * 0.04 }}
              data-testid={`feed-row-${i}`}
              className="flex items-center gap-4 md:gap-6 px-5 md:px-7 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
            >
              <div className="w-11 h-11 shrink-0 grid place-items-center rounded-sm border border-white/10 bg-white/5">
                {Icon ? <Icon className="w-6 h-6 text-zinc-300" /> : <Cpu className="w-5 h-5 text-aurora-cyan" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold uppercase tracking-tight truncate">
                  {it.brand} {it.model}
                </div>
                <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider truncate">
                  {it.year} · ECU {it.ecu}
                </div>
              </div>
              {it.hp ? (
                <div className="hidden sm:flex items-center gap-1.5 shrink-0 w-20 justify-end">
                  <Gauge className="w-3.5 h-3.5 text-aurora-cyan" />
                  <span className="font-mono text-sm font-bold text-aurora-cyan">{it.hp}</span>
                  <span className="font-mono text-[9px] text-zinc-500">CH</span>
                </div>
              ) : <div className="hidden sm:block w-20" />}
              <div className="hidden md:block shrink-0">
                <span className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border border-aurora-cyan/30 text-aurora-cyan bg-aurora-cyan/5">
                  {it.service}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 w-16 justify-end">
                <Coins className="w-3.5 h-3.5 text-zinc-500" />
                <span className="font-mono text-sm font-semibold">{it.credits}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
